import React, { useState, useEffect, useRef } from "react";
import axios from 'axios';
import { FileEarmarkPdf } from 'react-bootstrap-icons';
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { get } from "../services/ApiService";
import "./custom-style.css";
import { Line } from "react-chartjs-2";
import CanvasJSReact from "@canvasjs/react-charts";
import usePermissionCheck from "../hooks/usePermissionCheck";
const CanvasJSChart = CanvasJSReact.CanvasJSChart;



const Dashboard = () => {
  const canView = usePermissionCheck(["general.dashboard"]);
  const canTracker = usePermissionCheck(["order.tracker"]);
  const canCourierJobs = usePermissionCheck(["order.courier_jobs"]);
  const canDashboardStats = usePermissionCheck(["general.dashboard_stats"]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRLoading, setIsRLoading] = useState(false);
  const [isNotFound, setNotFound] = useState(false);
  const [notStartedCount, setNotStartedCount] = useState(0);
  const [totalPMedOrders, setTotalPMedOrders] = useState(0);
  const [pendingPMedOrders, setPendingPMedOrders] = useState(0);
  const [approvedPMedOrders, setapprovedPMedOrders] = useState(0); // Initialize as null initially
  const [productData, setProductData] = useState(null);
  const [chartData, setChartData] = useState({ datasets: [] });
  const [productWithoutCategory, setProductWithoutCategory] = useState(0);
  const [pmedWithoutQuestions, setPmedWithoutQuestions] = useState(0);
  const [variationWithoutWeight, setVariationWithoutWeight] = useState(0);
  const [counts, setCounts] = useState({});
  const [missingStats, setMissingStats] = useState({});
  const [labels, setLabels] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    getShippingLabels();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const res = await get(`api/dashboard/stats`);
      console.log(res);
      if (res.success) {
        const formattedData = formatChartData(res.chartData);
        setChartData(formattedData);
        setMissingStats(res.stats);

        // Prepare options for product chart
        const productOptions = {
          animationEnabled: true,
          title: {
            text: "GSL vs P-Med Products",
          },
          data: [
            {
              type: "pie",
              startAngle: 240,
              indexLabel: "{label} - {y}",
              dataPoints: [
                { label: "GSL Products", y: res.gslProductsCount },
                { label: "P-Med Products", y: res.pmedProductsCount },
              ],
            },
          ],
        };

        // Set product data options
        setProductData(productOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchOrderStats();
    const interval = setInterval(() => {
      fetchOrderStats();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchOrderStats = async () => {
    try {
      const res = await get(`api/dashboard/orders/stats?platform=PILLPHARM`);
      console.log(res);
      if (res.success) {
        setCounts(res.counts);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const formatChartData = (data) => {
    const months = data.map((item) => ({
      label: new Date(0, item.month - 1).toLocaleString("default", {
        month: "short",
      }),
      gsl: item.GSL,
      pMed: item.PMed,
    }));

    return {
      datasets: [
        {
          type: "line",
          name: "GSL Orders",
          showInLegend: true,
          dataPoints: months.map((month) => ({
            label: month.label,
            y: month.gsl,
          })),
          fillOpacity: 0.3,
          color: "rgba(54,158,173,.7)",
        },
        {
          type: "line",
          name: "P-Med Orders",
          showInLegend: true,
          dataPoints: months.map((month) => ({
            label: month.label,
            y: month.pMed,
          })),
          fillOpacity: 0.3,
          color: "rgba(210,59,123,.7)",
        },
      ],
    };
  };

  const options = {
    data: chartData.datasets,
  };


  const getShippingLabels = async () => {
    try {
      setIsLoading(true);
      const res = await get(`api/orders/shipping/labels?date=${selectedDate}`);
      setIsLoading(false);
  
      if (res.success) {
        setLabels(res.files)
      }else{
        setLabels([])
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching PDFs:', error);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setIsRLoading(true);
      const baseUrl = process.env.REACT_APP_API_ENDPOINT;
      const response = await axios.get(`${baseUrl}/api/inventory/pos/daily`, {
        responseType: 'blob', 
        timeout: 30000, 
      });

      // Create blob link to download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary anchor element
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'pharmanest-pos-report.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download POS report. Please try again.');
    } finally {
      setIsRLoading(false);
    }
  };

  return (
    <>
      <Layout>
<div className="row mb-4 align-items-center">
  {/* Left side: Heading */}
  <div className="col-12 col-md-6 mb-3 mb-md-0">
    <h2 className="fs-4 mb-1">Dashboard</h2>
    <p className="mb-0">Overall stats about our portal</p>
  </div>

  {/* Right side: Button */}
  <div className="col-12 col-md-6 d-flex justify-content-md-end gap-4">
    {canView && (
    <button
      className="btn btn-primary btn-sm w-100 w-md-auto"
      onClick={handleDownloadReport}
      disabled={isRLoading}
    >
      {isRLoading ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Generating Report...
        </>
      ) : (
        'POS Report for Pharmanest'
      )}
    </button>
    )}
    {canCourierJobs && (
    <Link className="btn btn-primary btn-sm w-100 w-md-auto" to={`/orders/courier-jobs`}>
      Courier Jobs
    </Link>
    )}
  </div>
</div>


{canDashboardStats && <div className="row mb-4">
  {/* Left side: Table */}
  <div className="col-12 col-lg-8 mb-4 mb-lg-0">
    <h6>Product Missing Log</h6>
    <div className="table-responsive">
      <table className="table table-hover table-striped table-bordered table-swatches">
        <tbody>
          <tr>
            <td>Missing Weight</td>
            <td>{missingStats?.missingWeight || 0}</td>
            <td>Variation missing weight information.</td>
            <td><Link to={`/missing-values?type=missing-weight`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Format</td>
            <td>{missingStats?.missingFormat || 0}</td>
            <td>Variation missing format details.</td>
            <td><Link to={`/missing-values?type=missing-format`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Dimension</td>
            <td>{missingStats?.missingDimensions || 0}</td>
            <td>Variation missing width, height or length.</td>
            <td><Link to={`/missing-values?type=missing-dimensions`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Service</td>
            <td>{missingStats?.missingService || 0}</td>
            <td>Variation missing postage.</td>
            <td><Link to={`/missing-values?type=missing-service`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Custom Information</td>
            <td>{missingStats?.missingCustomsInfo || 0}</td>
            <td>Variation missing goods description, hscode or extended goods description.</td>
            <td><Link to={`/missing-values?type=missing-customs-info`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Questions</td>
            <td>{missingStats?.pmedWithoutQuestions}</td>
            <td>Products missing associated questions.</td>
            <td><Link to={`/missing-values?type=pmed-without-questions`}>View</Link></td>
          </tr>
          <tr>
            <td>Missing Sql Variation</td>
            <td>{missingStats?.missingSqlVariation || 0}</td>
            <td>Variation missing in sql database</td>
            <td><Link to={`/missing-values?type=missing-sql-variation`}>View</Link></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  {/* Right side: Stats cards */}
  <div className="col-12 col-lg-4">
    <div className="row g-3">
      <div className="col-6 col-md-12">
        <div className="card rounded-4 h-100" style={{ background: "transparent" }}>
          <div className="card-body p-4">
            <h6 className="mb-2" style={{ fontSize: "12px" }}>PENDING P APPROVAL</h6>
            <span className="fs-4 text-primary">{counts?.PENDING}</span>
          </div>
        </div>
      </div>

      <div className="col-6 col-md-12">
        <div className="card rounded-4 h-100" style={{ background: "transparent" }}>
          <div className="card-body p-4">
            <h6 className="mb-2" style={{ fontSize: "12px" }}>READY FOR RM</h6>
            <span className="fs-4 text-primary">{counts?.SEND_TO_RM}</span>
          </div>
        </div>
      </div>

      <div className="col-6 col-md-12">
        <div className="card rounded-4 h-100" style={{ background: "transparent" }}>
          <div className="card-body p-4">
            <h6 className="mb-2" style={{ fontSize: "12px" }}>SENT TO RM</h6>
            <span className="fs-4 text-primary">{counts?.PROCESSED}</span>
          </div>
        </div>
      </div>

      <div className="col-6 col-md-12">
        <div className="card rounded-4 h-100" style={{ background: "transparent" }}>
          <div className="card-body p-4">
            <h6 className="mb-2" style={{ fontSize: "12px" }}>SHIPPED</h6>
            <span className="fs-4 text-primary">{counts?.SHIPPED || 0}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>}


        <div className="card mb-4 rounded-4 p-7">
          <div className="card-header bg-transparent px-0 pt-0 pb-7">
            <div className="row align-items-center justify-content-between mb-4">
              <div className="col-6">
                <h4 className="mb-0">Shipping Labels</h4>
              </div>
              <div className="col-6 d-flex align-items-center justify-content-end">
                <div className="d-flex align-items-center">
                  <input 
                    type="date" 
                    className="form-control" 
                    id="labelDate"
                    style={{ width: '180px' }}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body px-0 pt-7 pb-0">
            <div className="row">
              {isLoading && (
                <>
                <div className="col-12 text-center">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                </>
              )}
              {!isLoading && labels.length > 0 && labels.map((label, index) => {
                const fileName = label.split('/').pop();

                return (
<div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={index}>
  <div className="card border-0 shadow-sm h-100 hover-shadow">
    <div className="card-body d-flex flex-column align-items-start">
      <div className="d-flex align-items-center mb-2">
        <FileEarmarkPdf size={24} className="text-danger me-2" />
      </div>
      <p className="text-muted small mb-3 text-break">
        {fileName}
      </p>
      <a
        href={label}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-outline-primary mt-auto"
      >
        View Label
      </a>
    </div>
  </div>
</div>

                );
              })}
            </div>
          </div>
        </div>




        {canDashboardStats && (
          <>
            <div className="row">
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-body">
                    <h6 className=" mb-4"> Sales Statistics</h6>
                    <div className="chart-container mt-4">
                      <CanvasJSChart options={options} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="chart-container">
                  {productData ? (
                    <CanvasJSChart options={productData} />
                  ) : (
                    <p className="text-center">Loading product statistics...</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Layout>
    </>
  );
};

export default Dashboard;
